/** @module qt.monitor
 *  @description 打点行为, 相关模块`qt.log`.
 */
import webMonitor from '@qt-base/monitor';
import * as Cookies from 'js-cookie';
import { invokeSchemaByIframe } from '../core';
import { inApp, inIosApp } from '../env';
import { invokeSchema, checkPageActive } from '../base';
import config from '../config';

const NODE_ENV = process.env.NODE_ENV as 'production' | 'staging';
const isStaging = process.env.NODE_ENV === 'staging';

let pageViewEntryCache: {
  page_entry_ts: number;
  page_id?: string;
  page_extend?: Record<string, any>;
} = { page_entry_ts: new Date().getTime() };

export enum LogKeyEnum {
  ACTION_EVENT = 'ActionEvent',
  PAGE_VIEW_ENTRY = 'PageViewEntry',
  PAGE_VIEW_EXIT = 'PageViewExit',
  EXPOSE = 'Expose',
  DEBUG_INFO = 'DebugInfo',
}

export enum DebugTypeEnum {
  INFO = 'info',
  ERROR = 'error',
}

/** @deprecated 日志类型 */
export enum LogLabelEnum {
  ENTER = 'enter',
  CLICK = 'click',
  EXIT = 'exit',
  VS2 = 'vs2',
  POPUP = 'popup',
  PLAY = 'play',
  LOAD = 'load',
}

export interface EventParams {
  logKey?: LogKeyEnum;
  data?: Record<string, any>;
}

/**
 * 事件打点统计
 * @example
 * ```javascript
 * qt.monitor.event({logKey: 'ActionEvent', data: { event_id: 'openVip'}})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function event(params: {
  /** 打点类型，默认有 ActionEvent,PageViewEntry,PageViewExit,Expose	 */
  logKey: LogKeyEnum;
  /** 打点数据（格式见 新版日志格式 https://cfc.qingtingfm.com/pages/viewpage.action?pageId=98251385） */
  data?: Record<string, any>;
}): Promise<void | unknown> {
  const { logKey, data = {} } = params;

  // 多终端公共参数处理
  let reData = { ...data };
  reData.cur_url = window.location.href;
  if (document.referrer) reData.ref_url = document.referrer;
  // == 多终端页面进入公共参数存储
  if (logKey === LogKeyEnum.PAGE_VIEW_ENTRY) {
    const { page_id: pageID, page_extend: pageExtend = {} } = reData;
    const pageEntryTs = new Date().getTime();
    pageViewEntryCache = { page_entry_ts: pageEntryTs, page_id: pageID, page_extend: pageExtend };
    reData.page_entry_ts = pageEntryTs;
  } else {
    reData.page_entry_ts = pageViewEntryCache.page_entry_ts;
    // == 携带公共页面数据
    reData = { ...pageViewEntryCache, ...reData };
  }
  // == 站内外日志数据分开处理
  if (inApp) {
    if (isStaging) console.log('event logKey&Data:', logKey, reData);
    return new Promise((resolve, reject) => {
      // == 页面激活状态才发送打点
      checkPageActive(() => {
        // == ios内的曝光打点使用iframe方法，防止弹窗冲突
        if (inIosApp && logKey && logKey === 'Expose') {
          return invokeSchemaByIframe('qingtingfm://action.qingting.fm/sendLog', {
            logKey,
            logValue: JSON.stringify(reData),
          })
            .then(resolve)
            .catch(reject);
        }
        return invokeSchema('qingtingfm://action.qingting.fm/sendLog', {
          logKey,
          logValue: JSON.stringify(reData),
        })
          .then(resolve)
          .catch(reject);
      });
    });
  }
  const isPcDomain = /^(www\.)?(staging\.)?((qingting\.fm)|(qtfm\.cn))$/.test(document.domain);
  const isMobileDomain = /^((m\.)|(m-bak\.))?(staging\.)?((qingting\.fm)|(qtfm\.cn))$/.test(document.domain);
  const mobileDomain = isMobileDomain ? 'msite' : 'sss';
  data.app_name = data.app_name || (isPcDomain ? 'www' : mobileDomain);
  const keys = config[NODE_ENV || 'production'];

  data.user_id = data.user_id || Cookies.get(keys?.h5_user_id) || null;
  data['#U'] = data.user_id;
  if (isStaging) console.log('event logKey&Data:', logKey, data);
  webMonitor.event({ logKey, data });
  return Promise.resolve();
}

/** 新版进入页面事件打点入参 */
export interface PageViewEntryParams {
  /** 打点数据（格式见 新版日志格式）https://cfc.qingtingfm.com/pages/viewpage.action?pageId=98251385 */
  data: Record<string, any>;
}

/**
 * 新版进入页面事件打点
 * @example
 * ```javascript
 * qt.monitor.pageViewEntry({ data: {
    ref_url: 'https://sss.qingting.fm/qt-web/qt-jssdk/dist/demo/action/index.html',
    cur_url:'https://sss.qingting.fm/qt-web/qt-jssdk/dist/demo/action/log.html'
  }})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function pageViewEntry({ data }: PageViewEntryParams): Promise<void | unknown> {
  return event({
    logKey: LogKeyEnum.PAGE_VIEW_ENTRY,
    data,
  });
}

/**
 * 新版退出页面事件打点
 * @example
 * ```javascript
 * qt.monitor.pageViewExit({ data: {
    ref_url: 'https://sss.qingting.fm/qt-web/qt-jssdk/dist/demo/action/index.html',
    cur_url:'https://sss.qingting.fm/qt-web/qt-jssdk/dist/demo/action/log.html'
  }})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function pageViewExit(params: {
  /** 打点数据（格式见 新版日志格式） */
  data: Record<string, any>;
}): Promise<void | unknown> {
  const { data } = params;
  return event({
    logKey: LogKeyEnum.PAGE_VIEW_EXIT,
    data,
  });
}

/**
 * 新版点击事件打点
 * @example
 * ```
 * qt.monitor.actionEvent({data: { event_id: 'openVip'}})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function actionEvent(params: {
  /** 打点数据,格式见 https://cfc.qingtingfm.com/pages/viewpage.action?pageId=98251385 (TODO) */
  data: Record<string, any>;
}): Promise<void | unknown> {
  const { data } = params;
  return event({
    logKey: LogKeyEnum.ACTION_EVENT,
    data,
  });
}

/**
 * 新版曝光事件打点
 * @example
 * ```javascript
 * qt.monitor.expose({ data: { event_id: 'myPage'}})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function expose(params: {
  /** 打点数据,格式见 https://cfc.qingtingfm.com/pages/viewpage.action?pageId=98251385 (TODO) */
  data: Record<string, any>;
}): Promise<void | unknown> {
  const { data } = params;
  return event({
    logKey: LogKeyEnum.EXPOSE,
    data,
  });
}

/**
 * debug打点统计
 * @version: 不支持站外
 */
export function debugInfo({
  data,
}: {
  /** 打点数据,格式见 https://cfc.qingtingfm.com/pages/viewpage.action?pageId=98251385 (TODO) */
  data: Record<string, any>;
}): Promise<void | unknown> {
  if (inApp) {
    return event({
      logKey: LogKeyEnum.DEBUG_INFO,
      data,
    });
  }
  // 站外暂不支持
  return Promise.resolve();
}

/** 行为日志打点 
 * @version: 不支持站外.
 * @example: 
 * ```javascript
 * qt.monitor.debug({ log: 'error' })
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
*/
export function debug(params: {
  /** 日志类型 */
  type?: DebugTypeEnum;
  /** 日志详情 */
  log?: string;
  /** 事件ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  eventId?: string;
  /** 日志打印所在文件路径 */
  path?: string;
}): Promise<unknown> {
  const { type = DebugTypeEnum.INFO, log = '', eventId, path } = params;
  const debugData: any = { type, log };
  if (path) debugData.path = path;
  return debugInfo({ data: { event_id: eventId, debug_data: debugData } });
}

/** 构建系统事件打点入参 */
export interface BMSEventParams {
  /**  事件类型 PageViewEntry|PageViewExit|ActionEvent|Expose */
  logKey?: LogKeyEnum;
  /**  事件 ID，命名规则见新版日志打点 https://cfc.qingtingfm.com/pages/viewpage.action?pageId=98256041 */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  eventId: string;
  /**  节点类型 */
  nodeType: string;
  /**  节点ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  nodeId: string;
  /**  项目ID，可选 */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  projectId?: string;
  /** 页面ID，可选 */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  pageId?: string;
  /** 剩余打点数据（格式见 新版日志格式 https://cfc.qingtingfm.com/pages/viewpage.action?pageId=98251385） */
  data: Record<string, any>;
}

/**
 * BMS系统打点方法
 * @example
 * ```javascript
 *  qt.monitor.bmsEvent({logKey: 'ActionEvent', eventId: 'click.modal', nodeType: 'modal', nodeId: 'XXXXXX'})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function bmsEvent({
  logKey,
  eventId,
  nodeType,
  nodeId,
  projectId,
  pageId,
  data,
}: BMSEventParams): Promise<unknown> {
  const bmsPageExtend: any = {};
  if (pageId || window.__BUILDER_PAGE_ID) bmsPageExtend.page_id = pageId || (window as any).__BUILDER_PAGE_ID;
  if (projectId || window.__BUILDER_PROJECT_ID) bmsPageExtend.project_id = projectId || window.__BUILDER_PROJECT_ID;
  const bmsExtend = {
    cid: nodeId,
    cname: nodeType,
    csid: nodeId ? nodeId.substr(-3) : undefined,
    ...bmsPageExtend,
  };
  const defaultData: any = {
    event_id: eventId,
    page_id: 'H5Bms',
    page_extend: { bms: bmsPageExtend },
  };
  const appendData: Record<string, any> = {};
  if (logKey === LogKeyEnum.ACTION_EVENT) defaultData.action_extend = { bms: bmsExtend };
  if (logKey === LogKeyEnum.EXPOSE) defaultData.expose_extend = { bms: bmsExtend };
  if (logKey === LogKeyEnum.DEBUG_INFO && data.debug_data) appendData.debug_data.bms = bmsExtend;

  if (!logKey) return Promise.resolve();
  return event({
    logKey,
    data: { ...defaultData, ...data, ...appendData },
  });
}

/** 构建系统页面进入打点 */
export function bmsPageViewEntry(options: BMSEventParams) {
  return bmsEvent({ ...options, logKey: LogKeyEnum.PAGE_VIEW_ENTRY });
}

/** 构建系统页面退出打点 */
export function bmsPageViewExit(options: BMSEventParams) {
  return bmsEvent({ ...options, logKey: LogKeyEnum.PAGE_VIEW_EXIT });
}

/** 构建系统页面行为打点 */
export function bmsActionEvent(options: BMSEventParams) {
  return bmsEvent({ ...options, logKey: LogKeyEnum.ACTION_EVENT });
}
/** 构建系统页面曝光打点 */
export function bmsExpose(options: BMSEventParams) {
  return bmsEvent({ ...options, logKey: LogKeyEnum.EXPOSE });
}
/** 构建系统行为日志打点 */

export function bmsDebugInfo(options: BMSEventParams) {
  return bmsEvent({ ...options, logKey: LogKeyEnum.DEBUG_INFO });
}

export interface BMSDebugParams {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  eventId: string;
  nodeType: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  nodeId: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  projectId: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  pageId: string;
  type?: DebugTypeEnum;
  log?: string;
  path: string;
}
export function bmsDebug({
  eventId,
  nodeType,
  nodeId,
  projectId,
  pageId,
  type = DebugTypeEnum.INFO,
  log = '',
  path,
}: BMSDebugParams): Promise<unknown> {
  const debugData: any = { type, log };
  if (path) debugData.path = path;
  return bmsDebugInfo({
    eventId,
    nodeType,
    nodeId,
    projectId,
    pageId,
    data: { debug_data: debugData },
  });
}

/** @ignore */
export const monitor = {
  event,
  pageViewEntry,
  pageViewExit,
  actionEvent,
  expose,
  debugInfo,
  debug,
  bmsEvent,
  bmsPageViewEntry,
  bmsPageViewExit,
  bmsActionEvent,
  bmsExpose,
  bmsDebugInfo,
  bmsDebug,
};
